import React, { FC, useEffect, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import styled from 'styled-components';
import usePlacesAutocomplete, {
  getGeocode,
  getZipCode,
} from 'use-places-autocomplete';

import ClearIcon from '../../assets/icons/clear-icon.svg';
import {
  colors,
  DISABLE_ADDRESS_AUTOCOMPLETE_OPTIMIZELY_KEY,
} from '../constants';
import { ifFeatureFlagOn } from '../utilities/feature-flags';
import { ADDRESS_REGEX } from '../utilities/formatting';
import { heapTrackClientError } from '../utilities/logging';

import { Button } from './Button';
import { TextInput, TextInputProps } from './TextInput';

type Suggestion = google.maps.places.AutocompletePrediction;
type PredictionSubstring = google.maps.places.PredictionSubstring;

interface AddressTextInputProps extends TextInputProps {
  onAutoComplete: (
    streetAddress: string,
    city: string,
    state: string,
    zipCode: string
  ) => void;
  onClear: Function;
}

interface StyledProps {
  isFocused?: boolean;
  displayValue?: boolean;
}

// #region -- Styles
const Container = styled.div`
  position: relative;
  width: 100%;
`;
Container.displayName = 'Container';

const ClearButton = styled(Button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  height: 1.5rem;
  width: 1.5rem;
  margin: 0;
  padding: 0;
  cursor: pointer;
  &:focus-visible {
    top: 0.9375rem;
    right: 0.8125rem;
    border: 0.0625rem solid ${colors.TEXT_PRIMARY};
    border-radius: 0.25rem;
    padding: 0.0625rem 0.0625rem;
    height: 1.75rem;
    width: 1.75rem;
  }
`;
ClearButton.displayName = 'ClearButton';

const SearchResultsWrapper = styled.div``;
SearchResultsWrapper.displayName = 'SearchResultsWrapper';

const SearchResults = styled.ul`
  position: absolute;
  width: 100%;
  z-index: 999;
  box-shadow: 0.0625rem 0.25rem 0.9375rem rgba(0, 0, 0, 0.15);
  border-radius: 0.125rem;
`;
SearchResults.displayName = 'SearchResults';

const SearchResult = styled.li<StyledProps>`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  line-height: 1.5rem;
  background-color: ${colors.DROPDOWN_BACKGROUND};
  color: ${colors.TEXT_INPUT};
  b {
    font-weight: 700;
  }
  ${(p: StyledProps) =>
    p.isFocused &&
    `
    border: none;
    background-color: ${colors.DROPDOWN_HOVER};
    cursor: pointer;
  `};
`;
SearchResult.displayName = 'SearchResult';
// #endregion

// Component ---------------------------------------------------------------------------------------
export const AddressTextInput: FC<AddressTextInputProps> = ({
  onAutoComplete,
  onClear,
  ...props
}) => {
  const [streetAddress, setStreetAddress] = useState(''); // Address obtained from main_text
  const [city, setCity] = useState(''); // City obtained from selected suggestion terms array
  const [state, setState] = useState(''); // State obtained from selected suggestion terms array
  const [zipCode, setZipCode] = useState(''); // Postal code obtained from getGeocode response
  const [index, setIndex] = useState(-1); // Index of currently focused item in suggestion list
  const [displayedValue, setDisplayedValue] = useState(''); // Temporarily displayed input value
  const focusedRef = React.useRef(null);

  const {
    name,
    value: formikValue,
    label,
    type,
    error,
    touched,
    disabled,
    maxLength,
    customPattern,
    onChange,
    onBlur,
    resetLabel,
    forwardRef,
  } = props;

  const inputRef = forwardRef ?? React.useRef(null);
  const {
    ready,
    suggestions: { status, data },
    setValue: setAutoCompleteSearchQuery,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['street_address'],
    },
    cacheKey: 'street-address',
    debounce: 300,
    callbackName: 'initGoogle',
  });

  // Google Places Service status; determines if we can render address suggestions or not
  const hasSuggestions = status === 'OK';

  // Collapses search results if the user clicks outside the component
  const ref = useOnclickOutside(() => {
    clearSuggestions();
  });

  // Clears the input field & collapses search results on x-button click
  const clearInput = () => {
    clearSuggestions();
    setIndex(-1);
    setAutoCompleteSearchQuery('');
    setDisplayedValue('');
    // Reset local state variables for each of the address components
    setStreetAddress('');
    setCity('');
    setState('');
    setZipCode('');
    onClear();
  };

  // Removes the region code and formats the address
  const formatAddress = (address: string) => {
    let formattedAddress = address;
    if (address && address.length) {
      const components = address.split(', ');
      formattedAddress = components
        .slice(0, components.length - 1)
        .join(', ')
        .trim();
    }
    return formattedAddress;
  };

  // Returns the formatted suggestions with un-/bolded regions
  const formatSuggestions = (
    address: string,
    matchedSubstrings: PredictionSubstring[]
  ) => {
    let i = 0;
    const substrings = [];
    matchedSubstrings.forEach(({ length, offset }) => {
      substrings.push(address.substring(i, i + (offset - i)));
      substrings.push(<b>{address.substring(offset, offset + length)}</b>);
      i = offset + length;
    });
    substrings.push(address.substring(i));
    return substrings;
  };

  // Blur handler for the input field; prevents premature formik validation
  const handleBlur = (e: React.FocusEvent) => {
    e.preventDefault();
    // Clear suggestions when input loses focus to another element on the apply page (w/ tab)
    if (
      e.relatedTarget !== document.getElementById('main') &&
      e.relatedTarget !== document.getElementById('clear-button')
    ) {
      clearSuggestions();
    }
    if (onBlur) onBlur(e);
  };

  // Change handler for updating the input value of the home address (street1) field
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoCompleteSearchQuery(e.target.value);
    setDisplayedValue(e.target.value);
    setIndex(-1);
    if (onChange) onChange(e);
  };

  // Handler for selecting a suggested address from the search results list
  const handleSelect =
    ({ description }: Suggestion) =>
    () => {
      clearSuggestions();
      setIndex(-1);
      // Split the full address into each address component (city, state, etc.)
      const addressComponents = description.split(', ');
      if (addressComponents.length >= 3) {
        setStreetAddress(addressComponents[0]);
        setCity(addressComponents[1]);
        setState(addressComponents[2]);
        setAutoCompleteSearchQuery(addressComponents[0], false);
        setDisplayedValue(addressComponents[0]);
      }
      // Get zip code of selected address with getGeocode call
      getGeocode({ address: description })
        .then((results) => getZipCode(results[0], false))
        .then((zip) => {
          setZipCode(zip);
        })
        .catch((e) => {
          if (
            typeof e === 'string' &&
            e.length &&
            !/OVER_QUERY_LIMIT/.test(e)
          ) {
            // Log error to Heap if it is not a rate limit error
            heapTrackClientError({
              errorName: 'GoogleAPIError',
              errorMessage: `Unexpected error from Google Places API: ${e}`,
            });
          }
        });
    };

  // Handler for keydown events; focuses suggestion on arrow (up/down) keys
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!hasSuggestions) return; // Skip if suggestions are not currently displayed in UI
    switch (e.key) {
      // Clears the suggestions & resets displayed value on 'Esc' keydown
      case 'Escape' || 'Esc':
        setDisplayedValue(formikValue);
        setIndex(-1);
        clearSuggestions();
        break;
      // Selects a suggestion by simulating a mouse-click event on 'Enter' keydown
      case 'Enter':
        e.preventDefault();
        if (index > -1 && index < data.length && focusedRef.current) {
          focusedRef.current.click();
        } else {
          clearSuggestions();
        }
        break;
      // Navigates downwards in the suggestions list (index += 1)
      case 'ArrowDown' || 'Down':
        e.preventDefault();
        if (index === data.length - 1) {
          setDisplayedValue(formikValue);
          setIndex(-1);
        } else if (index < data.length - 1) {
          const formattedAddress = formatAddress(data[index + 1].description);
          setDisplayedValue(formattedAddress);
          setIndex(index + 1);
        }
        break;
      // Navigates upwards in the suggestions list (index -= 1)
      case 'ArrowUp' || 'Up':
        e.preventDefault();
        if (index === 0) {
          setDisplayedValue(formikValue);
          setIndex(-1);
        } else if (index === -1) {
          const formattedAddress = formatAddress(
            data[data.length - 1].description
          );
          setDisplayedValue(formattedAddress);
          setIndex(data.length - 1);
        } else if (index > 0) {
          const formattedAddress = formatAddress(data[index - 1].description);
          setDisplayedValue(formattedAddress);
          setIndex(index - 1);
        }
        break;
      // Updates the input value when the pressed key corresponds to an alphanumeric character
      default:
        if (
          e.key.match(ADDRESS_REGEX) &&
          index !== -1 &&
          displayedValue !== formikValue
        ) {
          // Manually concat the char from e.key to formikValue to re-render suggestions...
          // ... and replace the old displayedValue with the updated (real) input value
          setIndex(-1);
          const newValue = formikValue.concat(e.key);
          setAutoCompleteSearchQuery(newValue, true);
          setDisplayedValue(newValue);
        }
        break;
    }
  };

  // Clears all address fields on 'Enter' keydown for the x-button
  const handleClearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') clearInput();
  };

  // Clear suggestions if user tabs focus away from the address input/x-button
  const handleClearBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    if (
      inputRef.current &&
      e.relatedTarget &&
      e.relatedTarget !== inputRef.current
    ) {
      clearSuggestions();
    }
  };

  // Updates the currently highlighted list item & index on mouse enter
  const handleMouseEnter = (suggestion: Suggestion, idx: number) => {
    if (suggestion && index !== idx) {
      setIndex(idx);
      const formattedAddress = formatAddress(suggestion.description);
      setDisplayedValue(formattedAddress);
    }
  };

  // Resets index & sets displayedValue to original input value on mouse leave
  const handleMouseLeave = () => {
    setIndex(-1);
    setDisplayedValue(formikValue);
  };

  // Generates a list of address suggestions based on the current input value
  const renderSuggestions = () =>
    data.map((suggestion: Suggestion, idx: number) => {
      const {
        place_id: placeId,
        description,
        matched_substrings: matchedSubstrings,
      } = suggestion;
      const formattedAddress = formatAddress(description);

      return (
        <SearchResult
          key={placeId}
          id={`ex-list-item-${idx}`}
          ref={idx === index ? focusedRef : undefined}
          onClick={handleSelect(suggestion)}
          onMouseEnter={() => handleMouseEnter(suggestion, idx)}
          isFocused={idx === index}
          role="option"
          aria-selected={idx === index}
        >
          {formatSuggestions(formattedAddress, matchedSubstrings)}
        </SearchResult>
      );
    });

  useEffect(() => {
    // Trigger onAutoComplete after fetching updated address components on select
    if (streetAddress) {
      onAutoComplete(streetAddress, city, state, zipCode ?? '');
    }
  }, [zipCode]);

  // Render ----------------------------------------------------------------------------------------
  if (ifFeatureFlagOn(DISABLE_ADDRESS_AUTOCOMPLETE_OPTIMIZELY_KEY)) {
    return (
      <Container>
        <TextInput
          name={name}
          value={formikValue}
          label={label}
          type={type}
          error={error}
          touched={touched}
          disabled={disabled}
          maxLength={maxLength}
          customPattern={customPattern}
          onChange={onChange}
          onBlur={onBlur}
        />
      </Container>
    );
  }

  return (
    <Container
      ref={ref}
      onKeyDown={handleKeyDown}
      role="combobox"
      aria-owns="ex-list-box"
      aria-haspopup="listbox"
      aria-expanded={hasSuggestions}
    >
      <TextInput
        name={name}
        value={index !== -1 && hasSuggestions ? displayedValue : formikValue}
        label={label}
        type={type}
        error={error}
        touched={touched}
        disabled={disabled}
        maxLength={maxLength}
        customPattern={customPattern}
        onChange={handleInput}
        onBlur={handleBlur}
        forwardRef={inputRef}
        resetLabel={resetLabel}
        aria-autocomplete="list"
        aria-controls="ex-list-box"
        aria-activedescendant={
          index !== null ? `ex-list-item-${index}` : undefined
        }
      />
      {formikValue !== '' && (
        <ClearButton
          id="clear-button"
          type="button"
          onClick={clearInput}
          onBlur={handleClearBlur}
          onKeyDown={handleClearKeyDown}
          aria-label="Clear address fields"
        >
          <ClearIcon />
        </ClearButton>
      )}
      {ready && hasSuggestions && (
        <SearchResults
          id="ex-list-box"
          role="listbox"
          onMouseLeave={handleMouseLeave}
        >
          {renderSuggestions()}
        </SearchResults>
      )}
    </Container>
  );
};
